import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BuscarPecasRow } from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import BuscaPecas from '../componentes/BuscaPecas';
import CampoBusca from '../componentes/CampoBusca';
import Ordenacao from '../componentes/Ordenacao';
import Segmentado from '../componentes/Segmentado';
import SeletorCliente from '../componentes/SeletorCliente';
import type { PaymentMethod, ReservationPayment } from '@core/types';
import {
  adicionarItemEncomenda, atualizarEncomenda, atualizarItemEncomenda, avisarEncomenda,
  converterEncomendaEmVenda, corrigirDatasDaEncomenda, criarEncomenda, enfileirarImpressao,
  estornarPagamentoEncomenda, limparAvisoEncomenda, listarEncomendas,
  pagamentosDeEncomendas, pecaCoringa, perfis, registrarPagamentoEncomenda, removerItemEncomenda,
  trocarVendedorDaEncomenda,
  type EncomendaItem, type EncomendaLinha, type ItemNovoDeEncomenda,
} from '../lib/consultas';
import {
  imprimirEncomenda, montarDocumentoEncomenda, type EncomendaImpressa,
} from '../lib/encomendaDoc';
import {
  podeCorrigirDatas, podeGerenciarVendas, podeTrocarVendedorDaEncomenda, useSessao,
} from '../lib/sessao';
import { brl, dataBR, dataHoraDaLoja, hojeISO, paraNumero, qtd } from '../lib/formato';
import { codigoDaPeca } from '../lib/codigoDaPeca';
import { casaBusca, porData, porNumero, porTexto } from '../lib/listas';
import { dataCurtaDeISO, validadePadrao } from '../lib/orcamento';
import {
  avisoAoLevar, combinadoDaLinha, contaFechada, nomeDaPecaEncomendada, PRAZO_PADRAO_DIAS,
  pesoNaLista, precoUnitDaLinha, situacaoEncomenda, textoDoPrazo, totalDaEncomenda,
} from '../lib/encomenda';
import { SKU_CORINGA } from '../lib/coringa';
import { mensagemDeErro } from '../lib/supabase';

// Aba Encomendas (migração 020) — a peça separada para o cliente que vai voltar.
//
// O RECORTE DE QUEM VÊ O QUÊ NÃO ESTÁ NESTA TELA: a RLS (§14) já entrega ao
// vendedor só as encomendas dele e ao gerente/dono todas. Por isso não há um
// único `if (papel === ...)` aqui — a lista que chega já é a lista certa, e
// duplicar a regra no front criaria um segundo lugar para ela envelhecer errado.
//
// "Remover" é cancelar (status), não apagar: promessa feita a cliente é
// histórico. O delete físico é do dono, direto no banco.

/** A primeira pergunta de quem abre a aba (28/10/2026): "o que está esperando
 *  o cliente?". As vencidas ganharam recorte próprio — antes só existiam no
 *  aviso do topo, e para achá-las era preciso trocar a ordem da lista. */
type Filtro = 'abertas' | 'vencidas' | 'encerradas' | 'todas';

/** O código da peça encomendada: o digitado na diversa, senão o de fábrica. */
const codigoDoItem = (i: EncomendaItem): string =>
  codigoDaPeca({ codigo_avulso: i.codigo_avulso, sku: i.peca?.sku, produto: i.peca });

/** O recorte pelo DINHEIRO (056/066) — a pergunta que o balcão mais faz depois
 *  de "qual vence primeiro": de quem ainda falta receber, e o que já está pago.
 *
 *  Desde a 068 "Paga, sem venda" é uma lista de EXCEÇÕES, não de trabalho de
 *  rotina: a venda nasce sozinha quando a conta fecha, então quitada sem venda
 *  é encomenda antiga ou conversão que foi recusada. Vazia é o normal. */
type FiltroDinheiro = 'todas' | 'a-combinar' | 'falta' | 'paga' | 'virou-venda';

const FILTROS_DINHEIRO = [
  { id: 'todas', rotulo: 'Todo dinheiro' },
  { id: 'falta', rotulo: 'Falta receber' },
  { id: 'paga', rotulo: 'Paga, sem venda' },
  { id: 'a-combinar', rotulo: 'Valor a combinar' },
  { id: 'virou-venda', rotulo: 'Já virou venda' },
] as const satisfies ReadonlyArray<{ id: FiltroDinheiro; rotulo: string }>;

type OrdemEncomenda =
  'resolver' | 'prazo-curto' | 'prazo-longo' | 'recente' | 'antiga' | 'cliente' | 'maior';

const ORDENS_ENCOMENDA = [
  // MAIS RECENTE PRIMEIRO, como nos orçamentos (pedido do dono, 04/09/2026).
  //
  // Era "a resolver primeiro" (pesoNaLista), e a razão de ser era proteger a
  // encomenda vencida de sumir no meio da lista. Essa proteção NÃO dependia da
  // ordenação: o aviso de prazo vencido fica no topo da tela, fora da lista, e
  // continua contando as expiradas em qualquer ordem. O que a ordem por peso
  // custava era o contrário — a encomenda aberta agora, que o cliente está
  // esperando ver anotada, nascia enterrada.
  //
  // Quem trabalha a fila de pendências troca para "a resolver primeiro", que
  // segue aqui, uma opção abaixo.
  { id: 'recente', rotulo: 'Aberta mais recente' },
  { id: 'resolver', rotulo: 'A resolver primeiro' },
  { id: 'prazo-curto', rotulo: 'Vence antes' },
  { id: 'prazo-longo', rotulo: 'Vence depois' },
  { id: 'antiga', rotulo: 'Aberta mais antiga' },
  { id: 'cliente', rotulo: 'Cliente (A–Z)' },
  { id: 'maior', rotulo: 'Maior valor' },
] as const satisfies ReadonlyArray<{ id: OrdemEncomenda; rotulo: string }>;

export default function Encomendas() {
  const cliente = useQueryClient();
  const [criando, setCriando] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>('abertas');
  const [busca, setBusca] = useState('');
  const [dinheiro, setDinheiro] = useState<FiltroDinheiro>('todas');
  const [vendedorId, setVendedorId] = useState('');
  const [ordem, setOrdem] = useState<OrdemEncomenda>('recente');
  const hoje = hojeISO();

  const encomendas = useQuery({ queryKey: ['encomendas'], queryFn: () => listarEncomendas() });

  // Os pagamentos (056) numa consulta só, para a tela não fazer N chamadas.
  // O "está paga?" é a SOMA destes — não existe coluna dizendo isso (regra 4).
  const ids = (encomendas.data ?? []).map((e) => e.id);
  const pagamentos = useQuery({
    queryKey: ['encomendas', 'pagamentos', ids.join(',')],
    queryFn: () => pagamentosDeEncomendas(ids),
    enabled: ids.length > 0,
  });
  const porEncomenda = new Map<string, ReservationPayment[]>();
  for (const pg of pagamentos.data ?? []) {
    const lista = porEncomenda.get(pg.reservation_id) ?? [];
    lista.push(pg);
    porEncomenda.set(pg.reservation_id, lista);
  }

  const recarregar = () => {
    cliente.invalidateQueries({ queryKey: ['encomendas'] });
    // O pagamento lança no caixa: o balcão ao vivo e a gaveta mudam junto.
    cliente.invalidateQueries({ queryKey: ['caixa'] });
    // E desde a 068 o pagamento que FECHA a conta registra a venda na hora —
    // a lista de vendas precisa saber, ou o colaborador só a veria no F5.
    cliente.invalidateQueries({ queryKey: ['vendas'] });
    // A ficha da peça mostra quanto está prometido — muda junto.
    cliente.invalidateQueries({ queryKey: ['reservado'] });
  };

  // Quanto já foi pago em cada encomenda — a soma dos pagamentos não estornados
  // (não existe coluna "pago", regra 4). O filtro de dinheiro e a ordem por
  // valor precisam disso, e o cartão de cada linha já recalculava o mesmo.
  const pagoDe = (id: string) => (porEncomenda.get(id) ?? [])
    .filter((pg) => !pg.estornado_em)
    .reduce((soma, pg) => soma + Number(pg.valor), 0);

  const visiveis = useMemo(() => {
    const filtradas = (encomendas.data ?? []).filter((e) => {
      if (filtro === 'abertas' && e.status !== 'ativa') return false;
      if (filtro === 'encerradas' && e.status === 'ativa') return false;
      if (filtro === 'vencidas' && situacaoEncomenda(e, hoje) !== 'expirada') return false;
      if (vendedorId && e.vendedor_id !== vendedorId) return false;

      if (dinheiro !== 'todas') {
        const combinado = totalDaEncomenda(e.itens);
        const pago = pagoDe(e.id);
        const quitada = contaFechada(pago, combinado);
        if (dinheiro === 'a-combinar' && combinado !== null) return false;
        if (dinheiro === 'virou-venda' && !e.sale_id) return false;
        // "Paga, sem venda": o cliente pagou tudo e a venda não existe. Desde
        // a 068 isso não acontece sozinho — é encomenda anterior à mudança ou
        // conversão recusada (teto de preço, carrinho do colega). É a fila do
        // que precisa de conserto, e o normal é ela estar vazia.
        if (dinheiro === 'paga' && (!quitada || e.sale_id)) return false;
        if (dinheiro === 'falta' && (combinado === null || quitada || e.sale_id)) return false;
      }

      // O NOME DAS PEÇAS ENTRA NA BUSCA, e é o que mais importa: quem liga
      // perguntando costuma dizer a peça ("a pastilha do Gol"), não o código.
      return casaBusca(busca, e.codigo, e.cliente_nome, e.cliente_contato,
                       e.vendedor, e.observacao,
                       e.itens.map(nomeDaPecaEncomendada).join(' '),
                       e.itens.map(codigoDoItem).join(' '));
    });

    return [...filtradas].sort((a, b) => {
      if (ordem === 'prazo-curto') return porData(a.expira_em, b.expira_em, 'asc');
      if (ordem === 'prazo-longo') return porData(a.expira_em, b.expira_em, 'desc');
      if (ordem === 'recente') return porData(a.created_at, b.created_at, 'desc');
      if (ordem === 'antiga') return porData(a.created_at, b.created_at, 'asc');
      if (ordem === 'cliente') {
        return porTexto(a.cliente_nome, b.cliente_nome) || porData(a.expira_em, b.expira_em, 'asc');
      }
      // "A combinar" é null e cai no fim por porNumero — encomenda sem valor
      // fechado não é encomenda barata, é encomenda por resolver.
      if (ordem === 'maior') {
        return porNumero(totalDaEncomenda(a.itens), totalDaEncomenda(b.itens), 'desc');
      }
      // 'resolver': o que exige ação primeiro (vencida, depois a que vence
      // antes). Deixou de ser o padrão em 04/09/2026, mas continua sendo a
      // ordem de quem senta para limpar a fila.
      if (ordem === 'resolver') {
        return pesoNaLista(a, hoje) - pesoNaLista(b, hoje)
          || a.expira_em.localeCompare(b.expira_em);
      }
      // O PADRÃO: a mais recente em cima, como na aba de orçamentos.
      return porData(a.created_at, b.created_at, 'desc');
    });
    // pagoDe fecha sobre `porEncomenda`, que é remontado a cada render a partir
    // de pagamentos.data — é esse dado que a lista precisa ver mudar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encomendas.data, pagamentos.data, filtro, busca, dinheiro, vendedorId, ordem, hoje]);

  const todas = encomendas.data ?? [];
  const aVencer = todas.filter((e) => situacaoEncomenda(e, hoje) === 'expirada').length;
  const abertas = todas.filter((e) => e.status === 'ativa').length;

  // A situação com a CONTAGEM no botão: quem chega na aba sabe de cara quantas
  // estão esperando cliente, sem ler a lista inteira.
  const situacoes = [
    { id: 'abertas', rotulo: `Abertas (${abertas})` },
    { id: 'vencidas', rotulo: `Prazo vencido (${aVencer})` },
    { id: 'encerradas', rotulo: 'Entregues e canceladas' },
    { id: 'todas', rotulo: 'Todas' },
  ] as const satisfies ReadonlyArray<{ id: Filtro; rotulo: string }>;

  const filtrando = busca.trim() !== '' || dinheiro !== 'todas' || vendedorId !== '';
  const limparFiltros = () => { setBusca(''); setDinheiro('todas'); setVendedorId(''); };
  // Dinheiro, vendedor e ordem ficam recolhidos: são do gerente que limpa a
  // fila, não de quem atende. Filtro ligado mantém o painel aberto — filtro
  // escondido é a lista "sumindo" sem ninguém entender por quê.
  const [maisFiltros, setMaisFiltros] = useState(false);
  const filtrosAbertos = maisFiltros || dinheiro !== 'todas' || vendedorId !== '' || ordem !== 'recente';

  // A equipe, para o filtro por vendedor. Todos veem todas as encomendas desde
  // 19/08 — então o filtro serve a qualquer perfil, não só à gerência.
  const equipe = useQuery({ queryKey: ['perfis'], queryFn: perfis, staleTime: 10 * 60_000 });

  return (
    <Layout
      titulo="Encomendas"
      acao={
        <button
          type="button"
          onClick={() => setCriando((v) => !v)}
          className="botao-primario px-3 py-2 text-xs"
        >
          {criando ? 'Fechar' : '+ Nova encomenda'}
        </button>
      }
    >
      <div className="space-y-4">
        {criando && (
          <NovaEncomenda aoCriar={() => { setCriando(false); recarregar(); }} />
        )}

        {!criando && (
          <p className="text-sm text-mute">
            Peça separada para o cliente que vai voltar. A encomenda{' '}
            <strong className="text-paper">não baixa o estoque</strong>: ela avisa, na ficha da
            peça, que aquela unidade tem dono até o prazo.
          </p>
        )}

        <section className="cartao space-y-3 p-4">
          <Segmentado itens={situacoes} valor={filtro} aoMudar={setFiltro} />

          <CampoBusca
            valor={busca}
            aoMudar={setBusca}
            placeholder="Buscar: nº EN-…, cliente, telefone, peça ou código da peça"
          />

          {filtrosAbertos ? (
            <div className="space-y-3 border-t border-edge pt-3">
              <Segmentado itens={FILTROS_DINHEIRO} valor={dinheiro} aoMudar={setDinheiro} />
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="rotulo" htmlFor="vendedor-encomenda">Separada por</label>
                  <select id="vendedor-encomenda" className="campo py-2" value={vendedorId}
                          onChange={(ev) => setVendedorId(ev.target.value)}>
                    <option value="">Todos</option>
                    {(equipe.data ?? []).map((p) => (
                      <option key={p.id} value={p.id}>{p.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <Ordenacao opcoes={ORDENS_ENCOMENDA} valor={ordem} aoMudar={setOrdem} />
                </div>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setMaisFiltros(true)}
                    className="text-xs font-bold text-dim underline">
              Mais filtros (dinheiro, vendedor, ordem)
            </button>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-dim">
              {filtrando
                ? `${visiveis.length} de ${encomendas.data?.length ?? 0}`
                : visiveis.length} encomenda(s)
            </p>
            {filtrando && (
              <button type="button" onClick={limparFiltros}
                      className="border border-edge px-3 py-1 text-xs font-bold text-dim">
                Limpar filtros
              </button>
            )}
          </div>
        </section>

        {aVencer > 0 && filtro === 'abertas' && (
          <Aviso tipo="alerta">
            {aVencer} encomenda(s) com prazo vencido. A peça já voltou a ser de quem chegar —
            ligue para o cliente ou encerre a promessa.{' '}
            <button type="button" className="font-bold underline"
                    onClick={() => setFiltro('vencidas')}>
              Ver só as vencidas
            </button>
          </Aviso>
        )}

        {encomendas.error ? <Aviso tipo="erro">{mensagemDeErro(encomendas.error)}</Aviso> : null}
        {encomendas.isLoading && <Carregando texto="Carregando encomendas" />}

        {visiveis.length > 0 ? (
          <ul className="space-y-2">
            {visiveis.map((e) => (
              <li key={e.id}>
                <CartaoEncomenda encomenda={e} hoje={hoje} aoMudar={recarregar}
                                 pagamentos={porEncomenda.get(e.id) ?? []} />
              </li>
            ))}
          </ul>
        ) : (
          !encomendas.isLoading && (
            <div className="cartao p-6 text-center text-sm text-mute">
              {filtrando && (encomendas.data?.length ?? 0) > 0
                ? 'Nenhuma encomenda casa com esses filtros.'
                : filtro === 'abertas'
                  ? 'Nenhuma encomenda aberta. Use "+ Nova encomenda" quando o cliente pedir para separar uma peça.'
                  : filtro === 'vencidas'
                    ? 'Nenhuma encomenda com prazo vencido.'
                    : filtro === 'encerradas'
                      ? 'Nenhuma encomenda entregue ou cancelada ainda.'
                      : 'Nenhuma encomenda registrada ainda.'}
            </div>
          )
        )}
      </div>
    </Layout>
  );
}

/**
 * UMA LINHA DO PEDIDO em montagem (066). `chave` é a identidade da LINHA, não
 * da peça: duas peças diversas são duas linhas do mesmo product_id, cada uma
 * com o seu nome e o seu valor — é o mesmo desenho do carrinho da venda (041).
 *
 * Quantidade e valor são TEXTO porque é o que a pessoa digita: "1.500" virando
 * 1,5 é a armadilha do pt-BR (regra 9), e quem converte é paraNumero().
 */
interface LinhaNova {
  chave: string;
  productId: string;
  sku: string;
  descricao: string;
  precoBalcao: number | null;
  estoque: number;
  localizacao: string | null;
  quantidade: string;
  /** O preço DA UNIDADE, como no carrinho da venda (041). O combinado da linha
   *  é ele vezes a quantidade — ver combinadoDaLinha(). */
  precoUnit: string;
  /** O nome escrito na peça diversa. Vazio nas cadastradas. */
  nome: string;
  /** O CÓDIGO da peça diversa (071): uso interno. Existe para tirar o código
   *  de dentro do NOME — o nome é o que vai no papel que o cliente leva. */
  codigo: string;
  /** O código da peça CADASTRADA (fabricante, OEM ou o da loja), só para
   *  mostrar: é o que o balcão confere antes de separar. */
  codigoCadastro: string;
}

/** O combinado desta linha: preço da unidade × quantidade. */
const combinadoDaLinhaNova = (l: LinhaNova): number | null =>
  combinadoDaLinha(paraNumero(l.precoUnit), paraNumero(l.quantidade));

/**
 * A CONTA DA LINHA À VISTA: "3 × R$ 50,00 … R$ 150,00". Só aparece com mais de
 * uma unidade, que é justamente quando a multiplicação deixa de ser óbvia — e
 * era onde o balcão via R$ 50 no total de três peças (01/09/2026).
 */
function ContaDaLinha({ quantidade, precoUnit }: {
  quantidade: number | null; precoUnit: number | null;
}) {
  if (quantidade === null || quantidade <= 1) return null;
  const combinado = combinadoDaLinha(precoUnit, quantidade);
  return (
    <p className="mt-2 flex items-baseline justify-between gap-2 border-t border-edge pt-2 text-sm">
      <span className="text-mute">
        {qtd(quantidade)} × {precoUnit === null ? '—' : brl(precoUnit)}
      </span>
      <strong className="font-mono tabular-nums">
        {combinado === null
          ? <span className="font-sans text-xs font-normal text-dim">preço a combinar</span>
          : brl(combinado)}
      </strong>
    </p>
  );
}

const paraTexto = (n: number) => n.toFixed(2).replace('.', ',');

/**
 * O CÓDIGO DA PEÇA, sempre com rótulo (pedido do dono, 28/10/2026). Já saía
 * desde 14/10, mas solto, sem dizer o que era — e sumia por completo quando
 * a peça diversa ficou sem código, que é justamente o caso a consertar. Agora
 * a falta aparece: na diversa, com o caminho para preencher.
 */
function CodigoDaPeca({ codigo, diversa = false }: { codigo: string; diversa?: boolean }) {
  if (codigo) {
    return (
      <span className="mt-0.5 inline-flex max-w-full items-baseline gap-1 border border-edge px-1.5 py-0.5 text-xs">
        <span className="text-dim">Cód.</span>
        <strong className="truncate font-mono text-paper">{codigo}</strong>
      </span>
    );
  }
  return (
    <span className={`mt-0.5 block text-xs ${diversa ? 'text-alerta' : 'text-dim'}`}>
      {diversa ? 'sem código — preencha em Mais ações › Mudar peças' : 'sem código cadastrado'}
    </span>
  );
}

function NovaEncomenda({ aoCriar }: { aoCriar: () => void }) {
  const [itens, setItens] = useState<LinhaNova[]>([]);
  const [nome, setNome] = useState('');
  // O CLIENTE CADASTRADO (067). Nulo continua sendo caso comum — quem passou
  // uma vez não tem cadastro, e exigir um faria o balcão pular a encomenda.
  // Com vínculo, a compra que começar aqui aparece no histórico da pessoa.
  const [clienteId, setClienteId] = useState<string | null>(null);
  const [contato, setContato] = useState('');
  const [observacao, setObservacao] = useState('');
  const [prazo, setPrazo] = useState(() => validadePadrao(hojeISO(), PRAZO_PADRAO_DIAS));
  // O dinheiro já na abertura (056): o sinal que o cliente deixa na hora, que é
  // o caso comum do balcão. O combinado deixou de ser campo — ele é a soma das
  // peças (066), e cada uma traz o seu.
  const [sinal, setSinal] = useState('');
  const [sinalForma, setSinalForma] = useState<PaymentMethod>('dinheiro');

  const alterar = (chave: string, campos: Partial<LinhaNova>) =>
    setItens((atuais) => atuais.map((l) => (l.chave === chave ? { ...l, ...campos } : l)));

  const adicionar = (p: BuscarPecasRow) => {
    const linha: LinhaNova = {
      chave: crypto.randomUUID(),
      productId: p.product_id,
      sku: p.sku,
      descricao: p.descricao,
      precoBalcao: p.preco_balcao,
      estoque: p.quantidade,
      localizacao: p.localizacao,
      quantidade: '1',
      precoUnit: p.preco_balcao != null ? paraTexto(Number(p.preco_balcao)) : '',
      nome: '',
      codigo: '',
      codigoCadastro: codigoDaPeca({ sku: p.sku, produto: p }),
    };
    setItens((atuais) => {
      // PEÇA DIVERSA: sempre uma linha NOVA. Cinco peças diversas na mesma
      // encomenda são cinco nomes diferentes — juntá-las por product_id seria
      // exatamente o bug que a 041 corrigiu na venda.
      // A ÚLTIMA ADICIONADA FICA EM CIMA (pedido do dono, 08/09/2026), a mesma
      // régua do carrinho da venda: quem acabou de bipar confere na hora, sem
      // rolar a lista até o fim.
      if (p.sku === SKU_CORINGA) return [linha, ...atuais];
      const i = atuais.findIndex((l) => l.productId === p.product_id);
      if (i === -1) return [linha, ...atuais];
      // Peça cadastrada repetida soma quantidade — e só. O preço digitado à mão
      // ("esse eu faço por 40") não se perde por um bip a mais: ele é da
      // UNIDADE, então continua valendo com duas, três ou dez.
      return atuais.map((l, k) => (
        k === i ? { ...l, quantidade: String((paraNumero(l.quantidade) ?? 0) + 1) } : l
      ));
    });
  };

  // PEÇA DIVERSA NUM TOQUE (28/10/2026), o mesmo botão da aba Vender: buscar
  // "9999" era um passo que só quem conhecia o truque sabia dar. A linha nasce
  // em cima e o cursor vai para "Que peça é esta?", que é o que falta.
  const coringa = useQuery({
    queryKey: ['peca-coringa'], queryFn: pecaCoringa, staleTime: 60 * 60_000,
  });
  const adicionarDiversa = () => {
    const p = coringa.data;
    if (!p) return;
    const chave = crypto.randomUUID();
    setItens((atuais) => [{
      chave,
      productId: p.id,
      sku: p.sku,
      descricao: p.descricao,
      precoBalcao: null,
      estoque: 0,
      localizacao: null,
      quantidade: '1',
      precoUnit: '',
      nome: '',
      codigo: '',
      codigoCadastro: '',
    }, ...atuais]);
    requestAnimationFrame(() => {
      document.getElementById(`nome-${chave}`)?.focus({ preventScroll: true });
    });
  };

  const remover = (chave: string) =>
    setItens((atuais) => atuais.filter((l) => l.chave !== chave));

  // O total é a soma das linhas, e some inteiro enquanto uma estiver sem valor
  // — a mesma régua do banco (total_da_encomenda). Linha sem valor é decisão
  // válida ("esse aí eu vejo o preço"), e aí a encomenda fica "a combinar".
  const total = totalDaEncomenda(itens.map((l) => ({ valor: combinadoDaLinhaNova(l) })));

  const criar = useMutation({
    mutationFn: async () => {
      if (itens.length === 0) throw new Error('Adicione ao menos uma peça.');
      if (!nome.trim()) throw new Error('Informe o nome do cliente.');
      if (!prazo) throw new Error('Informe até quando a peça fica separada.');
      const paraEnviar: ItemNovoDeEncomenda[] = itens.map((l) => {
        const q = paraNumero(l.quantidade);
        if (q === null || q <= 0) {
          throw new Error(`Quantidade de "${l.descricao}" precisa ser maior que zero.`);
        }
        // A mesma régua do banco (trigger da 066), aplicada aqui para o balcão
        // descobrir ANTES de apertar o botão — e não no erro do Postgres.
        if (l.sku === SKU_CORINGA && !l.nome.trim()) {
          throw new Error('Escreva o nome da peça diversa: é o que o cliente vai ler no papel.');
        }
        return {
          productId: l.productId,
          quantidade: q,
          // O banco guarda o COMBINADO DA LINHA (066), não o preço unitário:
          // a multiplicação é feita aqui, uma vez, e o papel do cliente e o
          // total da encomenda leem o mesmo número.
          valor: combinadoDaLinha(paraNumero(l.precoUnit), q),
          descricao: l.sku === SKU_CORINGA ? l.nome : null,
          // 071: o código acompanha o nome — e para no banco, não no papel.
          codigo: l.sku === SKU_CORINGA ? (l.codigo.trim() || null) : null,
        };
      });
      await criarEncomenda({
        itens: paraEnviar,
        clienteNome: nome,
        customerId: clienteId,
        clienteContato: contato,
        observacao,
        expiraEm: prazo,
        sinal: paraNumero(sinal),
        sinalForma,
      });
    },
    onSuccess: aoCriar,
  });

  return (
    <section className="cartao space-y-4 border-signal/50 p-4">
      <h2 className="text-base font-bold">Nova encomenda</h2>

      <div className="space-y-2">
        <p className="rotulo">1. Quais peças separar?</p>
        {/* Sem aoBiparExato: aqui o bip escolhe a peça do formulário, e agir
            sozinho já criou encomenda pela metade em tela parecida. */}
        <BuscaPecas onEscolher={adicionar} placeholder="Peça cadastrada: bipe o código ou digite o nome…" />
        {coringa.data && (
          <div>
            <button type="button" className="botao-secundario w-full" onClick={adicionarDiversa}>
              + Peça sem cadastro (diversa)
            </button>
            <p className="mt-1 text-center text-xs text-dim">
              para a peça que não está no sistema — você escreve o nome e o código
            </p>
          </div>
        )}
      </div>

      {itens.length > 0 && (
        <ul className="space-y-2">
          {itens.map((l) => {
            const avulsa = l.sku === SKU_CORINGA;
            return (
              <li key={l.chave} className="border border-edge p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    {avulsa ? (
                      <p className="text-xs font-bold uppercase tracking-wider text-signal-soft">
                        Peça sem cadastro
                      </p>
                    ) : (
                      <>
                        <p className="truncate font-bold">{l.descricao}</p>
                        <CodigoDaPeca codigo={l.codigoCadastro} />
                      </>
                    )}
                    {!avulsa && (
                      <p className={`font-mono text-xs ${l.estoque <= 0 ? 'text-erro' : 'text-ok'}`}>
                        {l.estoque <= 0 ? 'sem estoque' : `${qtd(l.estoque)} un em estoque`}
                        {l.localizacao ? ` · ${l.localizacao}` : ''}
                      </p>
                    )}
                  </div>
                  <button type="button" className="shrink-0 px-2 text-sm text-erro"
                          onClick={() => remover(l.chave)}>
                    tirar
                  </button>
                </div>

                {/* PEÇA DIVERSA: a linha precisa dizer o que É. Sem isto o papel
                    do cliente sai com "PEÇA DIVERSA — BALCÃO", que não descreve
                    nada, e o banco recusa a encomenda (trigger da 066). */}
                {avulsa && (
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <div className="min-w-0">
                      <label className="rotulo" htmlFor={`nome-${l.chave}`}>Que peça é esta? *</label>
                      <input id={`nome-${l.chave}`} className="campo py-2.5" value={l.nome}
                             placeholder="Ex.: Retrovisor Uno 98 LD"
                             onChange={(e) => alterar(l.chave, { nome: e.target.value })} />
                    </div>
                    {/* O CÓDIGO em campo próprio (071): o nome vai para o papel
                        do cliente, o código fica conosco. Antes os dois
                        disputavam o mesmo input, e o cliente lia o código. */}
                    <div className="min-w-0">
                      <label className="rotulo" htmlFor={`codigo-${l.chave}`}>Código da peça (não sai no papel)</label>
                      <input id={`codigo-${l.chave}`} className="campo py-2.5 font-mono" value={l.codigo}
                             placeholder="Ex.: 9.4212.20" autoCapitalize="characters" autoComplete="off"
                             onChange={(e) => alterar(l.chave, { codigo: e.target.value })} />
                    </div>
                  </div>
                )}

                {/* PREÇO DA UNIDADE, e não valor da linha: é assim que o
                    balcão fala ("três, cinquenta cada") e é o mesmo campo do
                    carrinho da venda. Guardar o valor já somado era o defeito
                    de 01/09/2026 — na peça diversa, que não tem tabela para
                    re-sugerir, a quantidade não multiplicava nada. */}
                <div className="mt-2 grid grid-cols-[6rem_1fr] gap-3">
                  <div>
                    <label className="rotulo" htmlFor={`qtd-${l.chave}`}>Qtd.</label>
                    <input id={`qtd-${l.chave}`} className="campo py-2.5" inputMode="decimal"
                           value={l.quantidade}
                           onChange={(e) => alterar(l.chave, { quantidade: e.target.value })} />
                  </div>
                  <div>
                    <label className="rotulo" htmlFor={`valor-${l.chave}`}>Preço un. (R$)</label>
                    <input id={`valor-${l.chave}`} className="campo py-2.5" inputMode="decimal"
                           placeholder="a combinar" value={l.precoUnit}
                           onChange={(e) => alterar(l.chave, { precoUnit: e.target.value })} />
                    {l.precoBalcao != null && paraNumero(l.precoUnit) !== Number(l.precoBalcao) && (
                      <button type="button" className="mt-1 text-xs text-signal-soft underline"
                              onClick={() => alterar(l.chave, {
                                precoUnit: paraTexto(Number(l.precoBalcao)),
                              })}>
                        usar tabela: {brl(Number(l.precoBalcao))}
                      </button>
                    )}
                  </div>
                </div>

                <ContaDaLinha quantidade={paraNumero(l.quantidade)}
                              precoUnit={paraNumero(l.precoUnit)} />
              </li>
            );
          })}
        </ul>
      )}

      {itens.some((l) => l.sku !== SKU_CORINGA && l.estoque <= 0) && (
        <Aviso tipo="alerta">
          Tem peça sem estoque registrado na lista. Dá para encomendar assim mesmo (é o caso de
          pedir ao fornecedor), mas combine o prazo com o cliente sabendo disso.
        </Aviso>
      )}

      {itens.length > 0 && (
        <>
          <p className="rotulo border-t border-edge pt-3">2. Para quem e até quando?</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <SeletorCliente
                rotulo="Cliente *"
                autoFocus
                clienteId={clienteId}
                clienteNome={nome}
                aoEscolher={({ id, nome: n, telefone }) => {
                  setClienteId(id);
                  setNome(n);
                  // O contato do cadastro só entra em campo VAZIO: se o balcão já
                  // digitou um número, foi porque é outro — "hoje me liga neste".
                  if (telefone && !contato.trim()) setContato(telefone);
                }}
              />
              {!clienteId && (
                <p className="mt-1 text-xs text-dim">
                  Atrelar ao cadastro liga esta encomenda ao histórico do cliente. Sem cadastro,
                  escreva o nome e siga — o papel sai igual.
                </p>
              )}
            </div>
            <div>
              <label className="rotulo" htmlFor="enc-contato">WhatsApp / telefone</label>
              <input id="enc-contato" className="campo" inputMode="tel" value={contato}
                     onChange={(e) => setContato(e.target.value)} placeholder="(53) 9…" />
              <p className="mt-1 text-xs text-dim">É por aqui que se avisa quando o prazo chega.</p>
            </div>
            <div>
              <label className="rotulo" htmlFor="enc-prazo">Separar até *</label>
              <input id="enc-prazo" type="date" className="campo" value={prazo}
                     min={hojeISO()} onChange={(e) => setPrazo(e.target.value)} />
              <p className="mt-1 text-xs text-dim">
                Depois do prazo a peça volta a ser de quem chegar.
              </p>
            </div>
            <div>
              <span className="rotulo">Total combinado</span>
              <p className="font-mono text-lg font-bold tabular-nums">
                {total === null ? <span className="text-dim">a combinar</span> : brl(total)}
              </p>
              <p className="mt-1 text-xs text-dim">
                {total === null
                  ? 'Tem peça sem valor — dá para abrir assim e acertar depois.'
                  : `Soma de ${itens.length} peça(s).`}
              </p>
            </div>
          </div>

          {/* O SINAL (056) é opcional: metade das encomendas é "me avisa quando
              chegar", sem valor e sem sinal. */}
          <p className="rotulo border-t border-edge pt-3">3. Deixou dinheiro? (opcional)</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="rotulo" htmlFor="enc-sinal">Deixou de sinal</label>
              <input id="enc-sinal" className="campo" inputMode="decimal" placeholder="0,00"
                     value={sinal} onChange={(e) => setSinal(e.target.value)} />
              <p className="mt-1 text-xs text-dim">Entra no caixa agora.</p>
            </div>
            <div>
              <label className="rotulo" htmlFor="enc-sinal-forma">Forma do sinal</label>
              <select id="enc-sinal-forma" className="campo" value={sinalForma}
                      onChange={(e) => setSinalForma(e.target.value as PaymentMethod)}>
                {(['dinheiro', 'pix', 'debito', 'credito', 'transferencia'] as const).map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="rotulo" htmlFor="enc-obs">Observação</label>
            <input id="enc-obs" className="campo" value={observacao}
                   onChange={(e) => setObservacao(e.target.value)}
                   placeholder="Ex.: cliente vai passar sexta de manhã" />
          </div>

          {criar.error ? <Aviso tipo="erro">{mensagemDeErro(criar.error)}</Aviso> : null}

          <button type="button" className="botao-primario w-full py-3" disabled={criar.isPending}
                  onClick={() => criar.mutate()}>
            {criar.isPending ? 'Separando…' : `Separar ${itens.length} peça(s)`}
          </button>
        </>
      )}
    </section>
  );
}

export function CartaoEncomenda({
  encomenda: e, hoje, aoMudar, pagamentos,
}: {
  encomenda: EncomendaLinha; hoje: string; aoMudar: () => void;
  pagamentos: ReservationPayment[];
}) {
  const [prorrogando, setProrrogando] = useState(false);
  const [novoPrazo, setNovoPrazo] = useState(e.expira_em);
  // AS DATAS, PELO DONO (01/09/2026). Painel separado do "Prorrogar" porque as
  // duas operações são diferentes: prorrogar é balcão (o cliente pediu mais
  // prazo, e só anda para frente); corrigir é conserto de lançamento errado —
  // anda nos dois sentidos e mexe no dia da abertura, que sai no papel.
  const [corrigindo, setCorrigindo] = useState(false);
  // `created_at` é timestamp: fatiar os 10 primeiros caracteres daria o dia em
  // UTC, e a encomenda aberta às 21h de Rio Grande apareceria como do dia
  // seguinte. hojeISO(new Date(...)) devolve o dia LOCAL, que é o do papel.
  const [dataAbertura, setDataAbertura] = useState(() => hojeISO(new Date(e.created_at)));
  const [prazoCorrigido, setPrazoCorrigido] = useState(e.expira_em);
  const [motivoData, setMotivoData] = useState('');
  // O RECADO (050): "avise fulano, ou avise todos". Só faz sentido em
  // encomenda ativa — promessa encerrada não precisa de plantão.
  const { perfil, papel } = useSessao();
  const [avisando, setAvisando] = useState(false);
  const [paraQuem, setParaQuem] = useState<string>(e.aviso_para ?? '');
  const [recado, setRecado] = useState(e.aviso_texto ?? '');
  // QUEM SEPAROU, PELO DONO (18/09/2026): o dono lançou a encomenda que o
  // vendedor atendeu, e ela ficou no nome errado — no papel e em quem dá baixa.
  const [trocandoVendedor, setTrocandoVendedor] = useState(false);
  const [novoVendedor, setNovoVendedor] = useState('');
  const [motivoVendedor, setMotivoVendedor] = useState('');
  const equipe = useQuery({
    queryKey: ['perfis'], queryFn: perfis, enabled: avisando || trocandoVendedor,
  });

  const avisar = useMutation({
    mutationFn: () => avisarEncomenda({
      id: e.id, para: paraQuem || null, texto: recado,
    }),
    onSuccess: () => { setAvisando(false); aoMudar(); },
  });
  const limparAviso = useMutation({
    mutationFn: () => limparAvisoEncomenda(e.id),
    onSuccess: aoMudar,
  });

  // O DINHEIRO DA ENCOMENDA (056). O cliente pode não deixar nada, deixar um
  // sinal ou pagar tudo — e cada pagamento entra no caixa NA HORA. O pagamento
  // que FECHA a conta registra a venda junto (068), no banco: a encomenda seguir
  // na lista e a venda existir deixaram de ser escolhas excludentes.
  const validos = pagamentos.filter((pg) => !pg.estornado_em);
  const pago = validos.reduce((soma, pg) => soma + Number(pg.valor), 0);
  // 066: o combinado é a SOMA das peças, e some inteiro enquanto uma linha
  // estiver sem valor — a mesma régua de total_da_encomenda() no banco.
  const combinado = totalDaEncomenda(e.itens);
  const falta = combinado === null ? null : Math.max(combinado - pago, 0);
  const quitada = contaFechada(pago, combinado);
  const virouVenda = Boolean(e.sale_id);
  // O aviso antes da baixa: dinheiro recebido sem venda registrada some do
  // histórico se o "Cliente levou" passar por cima. Regra pura em encomenda.ts.
  const avisoLevar = avisoAoLevar({ pago, combinado, virouVenda });
  const [recebendo, setRecebendo] = useState(false);
  const [valorRecebido, setValorRecebido] = useState('');
  const [formaRecebida, setFormaRecebida] = useState<PaymentMethod>('dinheiro');
  const [confirmandoLevou, setConfirmandoLevou] = useState(false);
  const [combinando, setCombinando] = useState(false);
  const [maisAcoes, setMaisAcoes] = useState(false);
  const [confirmandoCancelar, setConfirmandoCancelar] = useState(false);

  const receber = useMutation({
    mutationFn: async () => {
      const v = paraNumero(valorRecebido);
      if (v === null || v <= 0) throw new Error('Quanto o cliente deixou?');
      await registrarPagamentoEncomenda({ id: e.id, valor: v, forma: formaRecebida });
    },
    onSuccess: () => { setRecebendo(false); setValorRecebido(''); aoMudar(); },
  });
  const estornar = useMutation({
    mutationFn: (pagamentoId: string) => estornarPagamentoEncomenda(pagamentoId),
    onSuccess: aoMudar,
  });
  const fecharVenda = useMutation({
    mutationFn: () => converterEncomendaEmVenda(e.id),
    onSuccess: aoMudar,
  });
  // O caminho de um toque do aviso: o cliente da encomenda PAGA chegou para
  // buscar. Fecha a venda (sem lançar caixa de novo) e dá a baixa em seguida.
  const fecharEDarBaixa = useMutation({
    mutationFn: async () => {
      await converterEncomendaEmVenda(e.id);
      await atualizarEncomenda(e.id, { status: 'atendida' });
    },
    onSuccess: () => { setConfirmandoLevou(false); aoMudar(); },
  });

  // O PAPEL DA ENCOMENDA (058). Existe porque o balcão vinha emitindo VALE
  // "a ver" para o cliente que encomendava — o vale era o único documento
  // impresso à mão. Agora a encomenda tem o dela, com a situação do pagamento
  // em tarja: é a primeira coisa que quem entrega precisa ler.
  const paraImprimir = (): EncomendaImpressa => ({
    codigo: e.codigo,
    cliente_nome: e.cliente_nome,
    cliente_contato: e.cliente_contato,
    // O papel recebe o nome JÁ RESOLVIDO de cada peça (066): na diversa é o que
    // o vendedor escreveu, e é ele que o cliente vai procurar na hora de retirar.
    itens: e.itens.map((i) => ({
      descricao: nomeDaPecaEncomendada(i),
      quantidade: Number(i.quantidade),
      valor: i.valor === null ? null : Number(i.valor),
    })),
    observacao: e.observacao,
    expira_em: e.expira_em,
    vendedor: e.vendedor,
    criada_em: e.created_at,
    pagamentos: validos.map((pg) => ({
      valor: Number(pg.valor), forma: pg.forma, recebido_em: pg.recebido_em,
    })),
  });
  const [filaErro, setFilaErro] = useState<string | null>(null);
  const [naFila, setNaFila] = useState(false);
  const imprimirNaLoja = useMutation({
    mutationFn: async () => {
      setFilaErro(null);
      await enfileirarImpressao({
        tipo: 'encomenda',
        titulo: `Encomenda ${e.codigo} — ${e.cliente_nome}`,
        documentoHtml: montarDocumentoEncomenda(paraImprimir()),
        refTable: 'reservations',
        refId: e.id,
      });
    },
    onSuccess: () => setNaFila(true),
    onError: (erro) => setFilaErro(mensagemDeErro(erro)),
  });

  const situacao = situacaoEncomenda(e, hoje);
  const encerrada = e.status !== 'ativa';
  // TODOS VEEM TODAS (19/08/2026) — mas mexer continua sendo de quem separou
  // (ou da gerência). Sem esconder os botões, o vendedor tocaria na encomenda
  // do colega e levaria o erro do banco na cara.
  const minha = e.vendedor_id === perfil?.id;
  const podeMexer = minha || podeGerenciarVendas(papel);

  const mudar = useMutation({
    mutationFn: (campos: Parameters<typeof atualizarEncomenda>[1]) =>
      atualizarEncomenda(e.id, campos),
    onSuccess: () => { setProrrogando(false); setConfirmandoLevou(false); aoMudar(); },
  });

  const aberturaOriginal = hojeISO(new Date(e.created_at));
  const corrigirDatas = useMutation({
    // Manda só o que MUDOU: a RPC entende nulo como "não mexe nesta data", e
    // reenviar a data igual gastaria a guarda do banco à toa (a encomenda com
    // pagamento recusaria a própria data de abertura de volta, sem necessidade).
    mutationFn: () => corrigirDatasDaEncomenda({
      id: e.id,
      expiraEm: prazoCorrigido !== e.expira_em ? prazoCorrigido : null,
      criadaEm: dataAbertura !== aberturaOriginal ? dataAbertura : null,
      motivo: motivoData,
    }),
    onSuccess: () => { setCorrigindo(false); setMotivoData(''); aoMudar(); },
  });
  const datasMudaram = prazoCorrigido !== e.expira_em || dataAbertura !== aberturaOriginal;

  const fecharTrocaDeVendedor = () => {
    setTrocandoVendedor(false);
    setNovoVendedor('');
    setMotivoVendedor('');
  };
  const trocarVendedor = useMutation({
    mutationFn: () => trocarVendedorDaEncomenda({
      id: e.id, vendedorId: novoVendedor, motivo: motivoVendedor,
    }),
    onSuccess: () => { fecharTrocaDeVendedor(); aoMudar(); },
  });

  const cor = situacao === 'expirada' ? 'text-alerta'
    : situacao === 'cancelada' ? 'text-erro'
      : situacao === 'atendida' ? 'text-dim' : 'text-ok';

  return (
    <article className={`cartao p-4 ${encerrada ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {/* O código da ENCOMENDA vem primeiro: é o que o cliente traz no
              papel e o que o balcão procura para entregar. */}
          <p className="font-mono text-sm font-bold text-signal-soft">{e.codigo}</p>
          <p className="text-base leading-snug">
            <strong>{e.cliente_nome}</strong>
            {e.cliente_contato && <span className="text-mute"> · {e.cliente_contato}</span>}
            {/* 067: o nome é o do papel; isto diz que ele tem cadastro atrás — e
                portanto que a venda desta encomenda entra no histórico dele. */}
            {e.customer_id && <span className="text-xs text-dim"> · no cadastro</span>}
          </p>
          {/* QUANDO E QUEM (pedido do dono, 28/10/2026): a hora exata, no
              relógio da loja, é a que se procura na gravação da câmera. Quem
              separou é essencial para quem está no balcão quando o cliente do
              colega chega. */}
          <p className="mt-0.5 text-xs text-mute">
            Feita em{' '}
            <strong className="font-mono text-paper" title="Horário de Brasília (Rio Grande)">
              {dataHoraDaLoja(e.created_at)}
            </strong>{' '}
            por <strong className="text-paper">{e.vendedor}</strong>
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className={`text-sm font-bold ${cor}`}>{textoDoPrazo(e, hoje)}</p>
          {/* O atalho só faz sentido com UMA peça: com três, a ficha de qual?
              A lista abaixo já nomeia todas, e o cartão não vira menu. */}
          {e.itens.length === 1 && e.itens[0]!.peca && e.itens[0]!.peca.sku !== SKU_CORINGA && (
            <Link to={`/peca/${e.itens[0]!.product_id}`} className="text-xs text-dim underline">
              ver peça
            </Link>
          )}
        </div>
      </div>

      <div className="mt-3">
          {/* AS PEÇAS DO PEDIDO (066), uma linha cada, com o CÓDIGO à vista —
              é o que se usa para pedir ao fornecedor e para achar na prateleira
              — e o valor combinado da linha. */}
          {e.itens.length === 0 ? (
            <p className="font-bold leading-snug text-alerta">encomenda sem peças</p>
          ) : (
            <ul className="divide-y divide-edge border-y border-edge">
              {e.itens.map((i) => (
                <li key={i.id} className="flex items-start justify-between gap-2 py-1.5">
                  <span className="min-w-0">
                    <span className="block font-bold leading-snug">
                      {Number(i.quantidade) !== 1 && (
                        <span className="font-mono text-xs text-dim">{qtd(i.quantidade)}× </span>
                      )}
                      {nomeDaPecaEncomendada(i)}
                    </span>
                    <CodigoDaPeca codigo={codigoDoItem(i)}
                                  diversa={i.peca?.sku === SKU_CORINGA || Boolean(i.descricao_avulsa)} />
                  </span>
                  <span className="shrink-0 font-mono text-sm tabular-nums text-mute">
                    {i.valor === null ? 'a combinar' : brl(Number(i.valor))}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {e.observacao && <p className="mt-2 text-xs text-mute">Obs.: {e.observacao}</p>}
          {/* A régua do dinheiro — é o que evita cobrar duas vezes ou entregar
              de graça, e o que diz se já dá para fechar a venda. */}
          <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
            {combinado === null ? (
              <span className="border border-edge px-1.5 py-0.5 text-dim">
                valor a combinar
              </span>
            ) : quitada ? (
              <span className="border border-ok/60 px-1.5 py-0.5 font-bold text-ok">
                PAGA · {brl(combinado)}
              </span>
            ) : pago > 0 ? (
              <span className="border border-alerta/60 px-1.5 py-0.5 font-bold text-alerta">
                sinal {brl(pago)} de {brl(combinado)} · faltam {brl(falta ?? 0)}
              </span>
            ) : (
              <span className="border border-edge px-1.5 py-0.5 text-mute">
                combinado {brl(combinado)} · nada pago
              </span>
            )}
            {/* 068: a tarja da venda registrada. Na encomenda paga e ainda não
                retirada ela convive com a de PAGA de propósito — são os dois
                estados ao mesmo tempo, e é isso que o balcão precisa ler. */}
            {virouVenda && (
              <span className="border border-signal px-1.5 py-0.5 font-bold text-signal-soft">
                venda registrada
              </span>
            )}
          </p>
          {e.aviso_em && (
            <p className="mt-1 border-l-2 border-signal pl-2 text-xs">
              <span className="font-bold text-signal-soft">
                {e.aviso_para ? 'Recado' : 'Recado para todos'}
              </span>
              {e.aviso_de_nome ? <span className="text-dim"> de {e.aviso_de_nome}</span> : null}
              : <span className="text-mute">{e.aviso_texto}</span>
            </p>
          )}
      </div>

      {mudar.error ? (
        <div className="mt-3"><Aviso tipo="erro">{mensagemDeErro(mudar.error)}</Aviso></div>
      ) : null}

      {!encerrada && !podeMexer && (
        <p className="mt-3 text-xs text-dim">
          Encomenda de <strong>{e.vendedor}</strong> — quem separou (ou o gerente)
          é quem dá baixa. Você vê para saber que a peça está prometida.
        </p>
      )}

      {!encerrada && podeMexer && (
        prorrogando ? (
          <div className="mt-3 space-y-2 border border-edge p-3">
            <label className="rotulo" htmlFor={`prazo-${e.id}`}>Novo prazo</label>
            <input id={`prazo-${e.id}`} type="date" className="campo py-2" value={novoPrazo}
                   min={hoje} onChange={(ev) => setNovoPrazo(ev.target.value)} />
            <div className="flex gap-2">
              <button type="button" className="botao-secundario flex-1 py-2 text-sm"
                      onClick={() => { setProrrogando(false); setNovoPrazo(e.expira_em); }}>
                Voltar
              </button>
              <button type="button" className="botao-primario flex-1 py-2 text-sm"
                      disabled={mudar.isPending || !novoPrazo}
                      onClick={() => mudar.mutate({ expira_em: novoPrazo })}>
                {mudar.isPending ? 'Salvando…' : 'Salvar novo prazo'}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            {/* AS TRÊS AÇÕES DO BALCÃO (28/10/2026). Eram doze botões na mesma
                fileira, e quem atende não sabia qual apertar. O que se faz
                todo dia fica à vista; o resto, em "Mais ações". */}
            <div className="flex flex-wrap gap-2">
              {/* Com dinheiro recebido e sem venda, a baixa direta perderia o
                  histórico — o clique abre o aviso em vez de executar. */}
              <button type="button" className="botao-primario flex-1 py-2.5 text-sm"
                      disabled={mudar.isPending}
                      onClick={() => (avisoLevar
                        ? setConfirmandoLevou((v) => !v)
                        : mudar.mutate({ status: 'atendida' }))}>
                Cliente levou
              </button>
              {!virouVenda && (
                <button type="button" className="flex-1 border border-ok px-3 py-2.5 text-sm font-bold text-ok"
                        onClick={() => {
                          setRecebendo((v) => !v);
                          setValorRecebido(falta !== null && falta > 0 ? falta.toFixed(2).replace('.', ',') : '');
                        }}>
                  Receber dinheiro
                </button>
              )}
              {quitada && !virouVenda && (
                <button type="button" className="botao-primario flex-1 px-3 py-2.5 text-sm"
                        disabled={fecharVenda.isPending}
                        onClick={() => fecharVenda.mutate()}>
                  {fecharVenda.isPending ? 'Fechando…' : 'Registrar a venda'}
                </button>
              )}
              {/* Duas vias com o pagamento na tarja, pela impressora da loja.
                  A janela do navegador ("Imprimir aqui") fica em Mais ações. */}
              <button type="button" className="botao-secundario flex-1 py-2.5 text-sm"
                      disabled={imprimirNaLoja.isPending}
                      onClick={() => imprimirNaLoja.mutate()}>
                {imprimirNaLoja.isPending ? 'Mandando…' : 'Imprimir'}
              </button>
            </div>

            <button type="button" aria-expanded={maisAcoes}
                    className="w-full border border-edge py-2 text-xs font-bold text-mute"
                    onClick={() => { setMaisAcoes((v) => !v); setConfirmandoCancelar(false); }}>
              {maisAcoes ? 'Menos ações ▲' : 'Mais ações: prazo, peças, recado, cancelar ▼'}
            </button>

            {maisAcoes && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                <button type="button" className="botao-secundario py-2 text-sm"
                        onClick={() => setProrrogando(true)}>
                  Dar mais prazo
                </button>
                <button type="button" className="botao-secundario py-2 text-sm"
                        onClick={() => setCombinando((v) => !v)}>
                  {combinado === null ? 'Peças e valores' : 'Mudar peças ou valores'}
                </button>
                <button type="button" className="botao-secundario py-2 text-sm"
                        onClick={() => setAvisando((v) => !v)}>
                  {e.aviso_em ? 'Mudar recado' : 'Avisar a equipe'}
                </button>
                {e.aviso_em && (
                  <button type="button" className="botao-secundario py-2 text-sm"
                          disabled={limparAviso.isPending}
                          onClick={() => limparAviso.mutate()}>
                    Tirar o recado
                  </button>
                )}
                <button type="button" className="botao-secundario py-2 text-sm"
                        onClick={() => imprimirEncomenda(paraImprimir())}>
                  Imprimir neste aparelho
                </button>
                {/* Só o dono. "Dar mais prazo" continua sendo o caminho do
                    balcão — este é o do conserto. */}
                {podeCorrigirDatas(papel) && (
                  <button type="button" className="botao-secundario py-2 text-sm"
                          onClick={() => setCorrigindo((v) => !v)}>
                    Corrigir datas
                  </button>
                )}
                {podeTrocarVendedorDaEncomenda(papel) && (
                  <button type="button" className="botao-secundario py-2 text-sm"
                          onClick={() => (trocandoVendedor ? fecharTrocaDeVendedor() : setTrocandoVendedor(true))}>
                    Trocar vendedor
                  </button>
                )}
                {/* Cancelar pede confirmação: era um toque só, ao lado de
                    botões do dia a dia, e desfazer exige o dono no banco. */}
                <button type="button"
                        className="border border-erro/60 px-3 py-2 text-sm font-bold text-erro"
                        disabled={mudar.isPending}
                        onClick={() => setConfirmandoCancelar(true)}>
                  Cancelar encomenda
                </button>
              </div>
            )}

            {maisAcoes && confirmandoCancelar && (
              <div className="space-y-2 border border-erro/60 p-3">
                <p className="text-sm">
                  Cancelar a encomenda <strong className="font-mono">{e.codigo}</strong> de{' '}
                  <strong>{e.cliente_nome}</strong>? A peça volta a ser de quem chegar.
                  {virouVenda ? (
                    <> A <strong>venda já registrada</strong> não é desfeita por aqui.</>
                  ) : pago > 0 && (
                    <> Já entraram <strong>{brl(pago)}</strong> no caixa por ela — estorne o
                    pagamento antes, se for devolver o dinheiro.</>
                  )}
                </p>
                <div className="flex gap-2">
                  <button type="button" className="botao-secundario flex-1 py-2 text-sm"
                          onClick={() => setConfirmandoCancelar(false)}>
                    Voltar
                  </button>
                  <button type="button"
                          className="flex-1 border border-erro px-3 py-2 text-sm font-bold text-erro"
                          disabled={mudar.isPending}
                          onClick={() => mudar.mutate({ status: 'cancelada' })}>
                    {mudar.isPending ? 'Cancelando…' : 'Sim, cancelar'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      )}

      {/* AS DATAS, PELO DONO (01/09/2026). FORA do gate de `encerrada` de
          propósito: "lancei no dia errado" costuma ser descoberto DEPOIS que a
          encomenda foi entregue, e é justamente aí que ela some da lista de
          abertas. Corrigir uma encomenda encerrada não muda nada no balcão —
          muda o histórico, que é o que estava errado. */}
      {podeCorrigirDatas(papel) && (corrigindo ? (
        <div className="mt-3 space-y-3 border border-edge p-3">
          <p className="text-xs text-mute">
            Correção de <strong className="text-paper">lançamento</strong>, não de combinado:
            muda o que ficou registrado errado. Para dar mais prazo ao cliente,
            o caminho é <strong>Dar mais prazo</strong>.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="rotulo" htmlFor={`abertura-${e.id}`}>Aberta em</label>
              <input id={`abertura-${e.id}`} type="date" className="campo py-2"
                     value={dataAbertura} max={hoje}
                     onChange={(ev) => setDataAbertura(ev.target.value)} />
              <p className="mt-1 text-xs text-dim">
                Hoje: {dataBR(e.created_at)}. A hora é preservada.
              </p>
            </div>
            <div>
              <label className="rotulo" htmlFor={`prazo-corrigido-${e.id}`}>Prazo</label>
              {/* Sem `min={hoje}`: é este campo que anda para TRÁS, e é o que
                  o botão Prorrogar não faz. O piso é o dia da abertura —
                  a mesma guarda que o banco aplica. */}
              <input id={`prazo-corrigido-${e.id}`} type="date" className="campo py-2"
                     value={prazoCorrigido} min={dataAbertura}
                     onChange={(ev) => setPrazoCorrigido(ev.target.value)} />
              <p className="mt-1 text-xs text-dim">Hoje: {dataCurtaDeISO(e.expira_em)}.</p>
            </div>
          </div>
          <div>
            <label className="rotulo" htmlFor={`motivo-data-${e.id}`}>Motivo (opcional)</label>
            <input id={`motivo-data-${e.id}`} type="text" className="campo py-2"
                   value={motivoData} maxLength={200}
                   placeholder="Ex.: cliente pediu por telefone ontem"
                   onChange={(ev) => setMotivoData(ev.target.value)} />
          </div>
          {corrigirDatas.error ? (
            <Aviso tipo="erro">{mensagemDeErro(corrigirDatas.error)}</Aviso>
          ) : null}
          <div className="flex gap-2">
            <button type="button" className="botao-secundario flex-1 py-2 text-sm"
                    onClick={() => {
                      setCorrigindo(false);
                      setDataAbertura(aberturaOriginal);
                      setPrazoCorrigido(e.expira_em);
                      setMotivoData('');
                      corrigirDatas.reset();
                    }}>
              Voltar
            </button>
            <button type="button" className="botao-primario flex-1 py-2 text-sm"
                    disabled={corrigirDatas.isPending || !datasMudaram
                              || !dataAbertura || !prazoCorrigido}
                    onClick={() => corrigirDatas.mutate()}>
              {corrigirDatas.isPending ? 'Salvando…' : 'Salvar datas'}
            </button>
          </div>
        </div>
      ) : encerrada && !trocandoVendedor && (
        // Encomenda encerrada não tem barra de ações — estes são os únicos
        // botões que ela ainda precisa mostrar, e só para o dono.
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className="border border-edge px-3 py-2 text-sm font-bold text-mute"
                  onClick={() => setCorrigindo(true)}>
            Corrigir datas
          </button>
          {podeTrocarVendedorDaEncomenda(papel) && (
            <button type="button" className="border border-edge px-3 py-2 text-sm font-bold text-mute"
                    onClick={() => setTrocandoVendedor(true)}>
              Trocar vendedor
            </button>
          )}
        </div>
      ))}

      {/* QUEM SEPAROU, PELO DONO (18/09/2026). Fora do gate de `encerrada` pelo
          mesmo motivo das datas: o lançamento no nome errado também costuma
          ser notado depois. O que a troca NÃO mexe está escrito no painel,
          porque é a primeira dúvida de quem troca. */}
      {podeTrocarVendedorDaEncomenda(papel) && trocandoVendedor && (
        <div className="mt-3 space-y-3 border border-edge p-3">
          <p className="text-xs text-mute">
            A pessoa escolhida passa a ser <strong className="text-paper">quem separou</strong>:
            o nome dela sai no papel do cliente e ela mexe na encomenda sem precisar
            do gerente. Quem recebeu cada pagamento continua o mesmo
            {virouVenda ? ', e a venda já registrada também' : ''}.
          </p>
          <div>
            <label className="rotulo" htmlFor={`vendedor-novo-${e.id}`}>Separada por</label>
            <select id={`vendedor-novo-${e.id}`} className="campo py-2" value={novoVendedor}
                    onChange={(ev) => setNovoVendedor(ev.target.value)}>
              <option value="">Escolha quem atendeu</option>
              {(equipe.data ?? [])
                .filter((p) => p.ativo && p.id !== e.vendedor_id)
                .map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
            <p className="mt-1 text-xs text-dim">Hoje: {e.vendedor}.</p>
          </div>
          <div>
            <label className="rotulo" htmlFor={`motivo-vendedor-${e.id}`}>Motivo (opcional)</label>
            <input id={`motivo-vendedor-${e.id}`} type="text" className="campo py-2"
                   value={motivoVendedor} maxLength={200}
                   placeholder="Ex.: lancei pelo vendedor que atendeu"
                   onChange={(ev) => setMotivoVendedor(ev.target.value)} />
          </div>
          {trocarVendedor.error ? (
            <Aviso tipo="erro">{mensagemDeErro(trocarVendedor.error)}</Aviso>
          ) : null}
          <div className="flex gap-2">
            <button type="button" className="botao-secundario flex-1 py-2 text-sm"
                    onClick={() => { fecharTrocaDeVendedor(); trocarVendedor.reset(); }}>
              Voltar
            </button>
            <button type="button" className="botao-primario flex-1 py-2 text-sm"
                    disabled={trocarVendedor.isPending || !novoVendedor}
                    onClick={() => trocarVendedor.mutate()}>
              {trocarVendedor.isPending ? 'Salvando…' : 'Trocar'}
            </button>
          </div>
        </div>
      )}

      {/* O AVISO ANTES DA BAIXA (25/08/2026): há dinheiro desta encomenda no
          caixa e nenhuma venda registrada. Aviso, não trava — quem atende vê o
          buraco e decide; o caminho certo está no botão. */}
      {!encerrada && podeMexer && confirmandoLevou && avisoLevar && (
        <div className="mt-3 space-y-2 border border-alerta/60 p-3">
          {avisoLevar === 'fechar-venda' ? (
            <p className="text-xs text-mute">
              Esta encomenda está <strong className="text-ok">paga</strong> e ainda não virou
              venda. Se der baixa agora, o dinheiro fica no caixa <strong>sem venda no
              histórico</strong> — e o estoque não baixa.
            </p>
          ) : (
            <p className="text-xs text-mute">
              Já entraram <strong>{brl(pago)}</strong> no caixa por esta encomenda e{' '}
              {combinado === null
                ? <>o valor combinado não foi registrado</>
                : <>faltam <strong>{brl(falta ?? 0)}</strong> para fechar a conta</>}.
              Se o cliente completou agora, use <strong>Receber</strong>: fechando a
              conta, a venda é registrada junto. Dando baixa assim, esse dinheiro
              não vira venda no histórico.
            </p>
          )}
          {fecharEDarBaixa.error ? (
            <Aviso tipo="erro">{mensagemDeErro(fecharEDarBaixa.error)}</Aviso>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button type="button" className="botao-secundario flex-1 py-2 text-sm"
                    onClick={() => setConfirmandoLevou(false)}>
              Voltar
            </button>
            {avisoLevar === 'fechar-venda' && (
              <button type="button" className="botao-primario flex-1 py-2 text-sm"
                      disabled={fecharEDarBaixa.isPending || mudar.isPending}
                      onClick={() => fecharEDarBaixa.mutate()}>
                {fecharEDarBaixa.isPending ? 'Fechando…' : 'Registrar a venda e dar baixa'}
              </button>
            )}
            <button type="button"
                    className="border border-alerta px-3 py-2 text-sm font-bold text-alerta"
                    disabled={mudar.isPending || fecharEDarBaixa.isPending}
                    onClick={() => mudar.mutate({ status: 'atendida' })}>
              {mudar.isPending ? 'Baixando…' : 'Levou mesmo assim'}
            </button>
          </div>
        </div>
      )}

      {!encerrada && podeMexer && avisando && (
        <div className="mt-3 space-y-2 border border-signal/40 p-3">
          <p className="text-xs text-mute">
            Quem for avisado passa a <strong>ver esta encomenda</strong> e recebe
            no sino — é o que evita prometer a mesma peça duas vezes quando você
            não estiver no balcão.
          </p>
          <div>
            <label className="rotulo" htmlFor={`para-${e.id}`}>Avisar</label>
            <select id={`para-${e.id}`} className="campo py-2" value={paraQuem}
                    onChange={(ev) => setParaQuem(ev.target.value)}>
              <option value="">Todos da loja</option>
              {(equipe.data ?? [])
                .filter((p) => p.ativo && p.id !== perfil?.id)
                .map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="rotulo" htmlFor={`recado-${e.id}`}>Recado</label>
            <input id={`recado-${e.id}`} className="campo py-2" value={recado}
                   placeholder="Ex.: passa hoje à tarde, já está pago"
                   onChange={(ev) => setRecado(ev.target.value)} />
          </div>
          {avisar.error ? <Aviso tipo="erro">{mensagemDeErro(avisar.error)}</Aviso> : null}
          <div className="flex gap-2">
            <button type="button" className="botao-secundario flex-1 py-2 text-sm"
                    onClick={() => setAvisando(false)}>Voltar</button>
            <button type="button" className="botao-primario flex-1 py-2 text-sm"
                    disabled={avisar.isPending || !recado.trim()}
                    onClick={() => avisar.mutate()}>
              {avisar.isPending ? 'Avisando…' : 'Deixar recado'}
            </button>
          </div>
        </div>
      )}
      {/* AS PEÇAS E OS VALORES (066). Cada linha tem o seu combinado, e o total
          é a soma — não existe um campo "valor da encomenda" para discordar
          dela. Só enquanto a encomenda está aberta e não virou venda: depois, a
          RLS recusa (§14.1), e mexer aqui faria o papel do cliente mentir. */}
      {!encerrada && podeMexer && combinando && (
        <PecasDaEncomenda encomenda={e} congelada={virouVenda} aoMudar={aoMudar}
                          aoFechar={() => setCombinando(false)} />
      )}

      {/* RECEBER: o dinheiro entra no caixa AGORA, que é quando ele entra na
          gaveta de verdade. */}
      {!encerrada && podeMexer && recebendo && !virouVenda && (
        <div className="mt-3 space-y-2 border border-ok/40 p-3">
          <p className="text-xs text-mute">
            Isto <strong>lança no caixa na hora</strong>. O cliente pode deixar um
            sinal hoje e completar depois; quando a conta fechar, a{' '}
            <strong>venda é registrada junto</strong> — e a encomenda continua na
            lista até ele levar a peça.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label className="rotulo" htmlFor={`receber-${e.id}`}>Recebi (R$)</label>
              <input id={`receber-${e.id}`} className="campo py-2" inputMode="decimal"
                     placeholder="0,00" value={valorRecebido}
                     onChange={(ev) => setValorRecebido(ev.target.value)} />
            </div>
            <div>
              <label className="rotulo" htmlFor={`forma-${e.id}`}>Forma</label>
              <select id={`forma-${e.id}`} className="campo py-2" value={formaRecebida}
                      onChange={(ev) => setFormaRecebida(ev.target.value as PaymentMethod)}>
                {(['dinheiro', 'pix', 'debito', 'credito', 'transferencia'] as const).map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
          </div>
          {receber.error ? <Aviso tipo="erro">{mensagemDeErro(receber.error)}</Aviso> : null}
          <div className="flex gap-2">
            <button type="button" className="botao-secundario flex-1 py-2 text-sm"
                    onClick={() => setRecebendo(false)}>Voltar</button>
            <button type="button" className="botao-primario flex-1 py-2 text-sm"
                    disabled={receber.isPending}
                    onClick={() => receber.mutate()}>
              {receber.isPending ? 'Lançando…' : 'Receber'}
            </button>
          </div>
        </div>
      )}

      {/* O extrato da encomenda: quem recebeu, quando e como. */}
      {validos.length > 0 && (
        <ul className="mt-3 space-y-1 border-l-2 border-edge pl-3 text-xs">
          {validos.map((pg) => (
            <li key={pg.id} className="flex items-center justify-between gap-2">
              {/* `recebido_em` é carimbo com hora: fatiá-lo como data pura saía
                  "18T18:55:15…/09/2026" (18/09). Desde 28/10 vai com a hora
                  exata da loja — o dinheiro também se confere na câmera. */}
              <span className="text-mute">
                {dataHoraDaLoja(pg.recebido_em)} · {pg.forma}
                {pg.recebido_por_nome ? ` · ${pg.recebido_por_nome}` : ''}
              </span>
              <span className="flex items-center gap-2">
                <strong className="font-mono tabular-nums">{brl(Number(pg.valor))}</strong>
                {podeMexer && !virouVenda && (
                  <button type="button" className="text-[0.625rem] uppercase text-dim underline"
                          disabled={estornar.isPending}
                          onClick={() => estornar.mutate(pg.id)}>
                    estornar
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {naFila && !filaErro && (
        <div className="mt-2">
          <Aviso tipo="ok">
            Encomenda <strong className="font-mono">{e.codigo}</strong> mandada para a
            impressora da loja — 2 vias, uma do cliente e uma da loja.
          </Aviso>
        </div>
      )}
      {filaErro && (
        <div className="mt-2">
          <Aviso tipo="alerta">
            <p>A impressora da loja não aceitou agora ({filaErro}).</p>
            <button type="button" className="botao-secundario mt-2"
                    onClick={() => imprimirEncomenda(paraImprimir())}>
              Imprimir aqui
            </button>
          </Aviso>
        </div>
      )}
      {/* 068: paga e sem venda deixou de ser passo do fluxo e virou pendência.
          A linha existe para quem abre a encomenda não precisar adivinhar por
          que há um botão de venda numa encomenda que o cliente já pagou. */}
      {!encerrada && podeMexer && quitada && !virouVenda && (
        <p className="mt-2 text-xs text-alerta">
          Paga, mas <strong>sem venda registrada</strong>. Encomenda quitada registra
          a venda sozinha — esta ficou de fora. Toque em <strong>Registrar a
          venda</strong>: se algo recusar, o motivo aparece aqui.
        </p>
      )}
      {fecharVenda.error ? (
        <div className="mt-2"><Aviso tipo="erro">{mensagemDeErro(fecharVenda.error)}</Aviso></div>
      ) : null}
      {estornar.error ? (
        <div className="mt-2"><Aviso tipo="erro">{mensagemDeErro(estornar.error)}</Aviso></div>
      ) : null}
      {limparAviso.error ? (
        <div className="mt-2"><Aviso tipo="erro">{mensagemDeErro(limparAviso.error)}</Aviso></div>
      ) : null}

      {encerrada && (
        <p className="mt-2 text-xs text-dim">
          {e.status === 'atendida' ? 'Entregue' : 'Cancelada'} · prazo era {dataCurtaDeISO(e.expira_em)}
          {virouVenda && ' · venda registrada'}
        </p>
      )}
    </article>
  );
}

/** O que a linha em espera precisa saber da peça: vem da busca ou do botão
 *  da peça diversa, que não passa pela busca. */
type PecaEscolhida = Pick<BuscarPecasRow, 'product_id' | 'sku' | 'descricao' | 'preco_balcao'>
  & Partial<Pick<BuscarPecasRow, 'codigo_fabrica' | 'codigo_oem'>>;

/**
 * AS PEÇAS DE UMA ENCOMENDA JÁ ABERTA (066).
 *
 * Aqui não há RPC: é insert/update/delete de tabela, com a RLS decidindo quem
 * pode — a mesma régua do prazo e do status, que sempre foram update direto.
 * A policy (§14.1) só deixa mexer enquanto a encomenda está ATIVA e não virou
 * venda; a tela esconde os botões antes disso para ninguém tomar 42501 na cara.
 *
 * Tirar uma linha APAGA de verdade, ao contrário da encomenda (que se cancela):
 * é o balcão corrigindo o pedido que ainda está montando, não desfazendo uma
 * promessa cumprida.
 */
function PecasDaEncomenda({
  encomenda: e, congelada, aoMudar, aoFechar,
}: {
  encomenda: EncomendaLinha; congelada: boolean; aoMudar: () => void; aoFechar: () => void;
}) {
  // A peça escolhida na busca espera aqui até ter quantidade, valor e — na
  // diversa — nome. Inserir no toque da busca criaria linha em branco no
  // pedido do cliente, e na diversa o banco recusaria (trigger da 066).
  const [pendente, setPendente] = useState<PecaEscolhida | null>(null);
  const coringa = useQuery({
    queryKey: ['peca-coringa'], queryFn: pecaCoringa, staleTime: 60 * 60_000,
  });
  const [novaQtd, setNovaQtd] = useState('1');
  /** O preço DA UNIDADE (ver LinhaNova): o combinado da linha é ele × qtd. */
  const [novoPreco, setNovoPreco] = useState('');
  const [novoNome, setNovoNome] = useState('');
  const [novoCodigo, setNovoCodigo] = useState('');

  const limpar = () => {
    setPendente(null); setNovaQtd('1'); setNovoPreco(''); setNovoNome(''); setNovoCodigo('');
  };

  const escolher = (p: PecaEscolhida) => {
    setPendente(p);
    setNovaQtd('1');
    setNovoPreco(p.preco_balcao != null ? paraTexto(Number(p.preco_balcao)) : '');
    setNovoNome('');
    setNovoCodigo('');
  };

  const adicionar = useMutation({
    mutationFn: async () => {
      if (!pendente) return;
      const q = paraNumero(novaQtd);
      if (q === null || q <= 0) throw new Error('Quantidade precisa ser maior que zero.');
      if (pendente.sku === SKU_CORINGA && !novoNome.trim()) {
        throw new Error('Escreva o nome da peça diversa: é o que o cliente vai ler no papel.');
      }
      await adicionarItemEncomenda({
        encomendaId: e.id,
        productId: pendente.product_id,
        quantidade: q,
        valor: combinadoDaLinha(paraNumero(novoPreco), q),
        descricao: pendente.sku === SKU_CORINGA ? novoNome : null,
        codigo: pendente.sku === SKU_CORINGA ? (novoCodigo.trim() || null) : null,
      });
    },
    onSuccess: () => { limpar(); aoMudar(); },
  });

  const total = totalDaEncomenda(e.itens);

  return (
    <div className="mt-3 space-y-3 border border-edge p-3">
      <p className="text-xs text-mute">
        Cada peça tem o <strong>seu preço por unidade</strong>; o total da encomenda é a soma
        das linhas (preço × quantidade).
        Peça sem valor deixa a encomenda <strong>a combinar</strong> — dá para acertar depois,
        mas ela só vira venda com tudo precificado e pago.
      </p>

      {e.itens.length === 0 ? (
        <p className="text-xs text-alerta">
          Esta encomenda está sem peças. Adicione ao menos uma.
        </p>
      ) : (
        <ul className="space-y-2">
          {e.itens.map((i) => (
            <LinhaDaEncomenda key={i.id} item={i} congelada={congelada} aoMudar={aoMudar} />
          ))}
        </ul>
      )}

      <p className="flex items-center justify-between border-t border-edge pt-2 text-sm">
        <span className="rotulo">Total</span>
        <strong className="font-mono tabular-nums">
          {total === null ? <span className="text-dim">a combinar</span> : brl(total)}
        </strong>
      </p>

      {!congelada && (
        pendente ? (
          <div className="space-y-2 border border-signal/40 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {pendente.sku === SKU_CORINGA ? (
                  <p className="text-xs font-bold uppercase tracking-wider text-signal-soft">
                    Peça sem cadastro
                  </p>
                ) : (
                  <>
                    <p className="truncate font-bold">{pendente.descricao}</p>
                    <CodigoDaPeca codigo={codigoDaPeca({ sku: pendente.sku, produto: pendente })} />
                  </>
                )}
              </div>
              <button type="button" className="shrink-0 px-2 text-sm text-erro" onClick={limpar}>
                trocar
              </button>
            </div>
            {pendente.sku === SKU_CORINGA && (
              <div>
                <label className="rotulo" htmlFor={`nome-nova-${e.id}`}>Que peça é esta? *</label>
                <input id={`nome-nova-${e.id}`} className="campo py-2" value={novoNome}
                       placeholder="Ex.: Retrovisor Uno 98 LD"
                       onChange={(ev) => setNovoNome(ev.target.value)} />
                <label className="rotulo mt-2 block" htmlFor={`codigo-nova-${e.id}`}>
                  Código da peça (não sai no papel)
                </label>
                <input id={`codigo-nova-${e.id}`} className="campo py-2 font-mono" value={novoCodigo}
                       placeholder="Ex.: 9.4212.20" autoCapitalize="characters" autoComplete="off"
                       onChange={(ev) => setNovoCodigo(ev.target.value)} />
              </div>
            )}
            <div className="grid grid-cols-[6rem_1fr] gap-2">
              <div>
                <label className="rotulo" htmlFor={`qtd-nova-${e.id}`}>Qtd.</label>
                <input id={`qtd-nova-${e.id}`} className="campo py-2" inputMode="decimal"
                       value={novaQtd} onChange={(ev) => setNovaQtd(ev.target.value)} />
              </div>
              <div>
                <label className="rotulo" htmlFor={`valor-nova-${e.id}`}>Preço un. (R$)</label>
                <input id={`valor-nova-${e.id}`} className="campo py-2" inputMode="decimal"
                       placeholder="a combinar" value={novoPreco}
                       onChange={(ev) => setNovoPreco(ev.target.value)} />
              </div>
            </div>
            <ContaDaLinha quantidade={paraNumero(novaQtd)} precoUnit={paraNumero(novoPreco)} />
            {adicionar.error ? <Aviso tipo="erro">{mensagemDeErro(adicionar.error)}</Aviso> : null}
            <button type="button" className="botao-primario w-full py-2 text-sm"
                    disabled={adicionar.isPending} onClick={() => adicionar.mutate()}>
              {adicionar.isPending ? 'Adicionando…' : 'Adicionar à encomenda'}
            </button>
          </div>
        ) : (
          <div>
            <p className="rotulo">Adicionar peça</p>
            <BuscaPecas onEscolher={escolher} autoFoco={false}
                        placeholder="Bipe o código ou digite a peça…" />
            {coringa.data && (
              <button type="button" className="botao-secundario mt-2 w-full text-sm"
                      onClick={() => escolher({
                        product_id: coringa.data!.id, sku: coringa.data!.sku,
                        descricao: coringa.data!.descricao, preco_balcao: null,
                      })}>
                + Peça sem cadastro (diversa)
              </button>
            )}
          </div>
        )
      )}

      <button type="button" className="botao-secundario w-full py-2 text-sm" onClick={aoFechar}>
        Fechar
      </button>
    </div>
  );
}

/** Uma linha do pedido aberto: quantidade, valor e — na diversa — o nome. O
 *  estado local nasce do servidor UMA vez (sem useEffect de sincronia): uma
 *  consulta lenta respondendo no meio da digitação apagaria o que o balcão
 *  acabou de escrever, que é o bug real da folha (CLAUDE.md). */
function LinhaDaEncomenda({
  item: i, congelada, aoMudar,
}: {
  item: EncomendaItem; congelada: boolean; aoMudar: () => void;
}) {
  const qtdInicial = String(Number(i.quantidade)).replace('.', ',');
  // O banco guarda o combinado DA LINHA; o campo mostra o preço da unidade.
  const precoInicial = (() => {
    const u = precoUnitDaLinha(i.valor === null ? null : Number(i.valor), Number(i.quantidade));
    return u === null ? '' : paraTexto(u);
  })();
  const [quantidade, setQuantidade] = useState(qtdInicial);
  const [precoUnit, setPrecoUnit] = useState(precoInicial);
  const [nome, setNome] = useState(i.descricao_avulsa ?? '');
  const [codigo, setCodigo] = useState(i.codigo_avulso ?? '');
  const avulsa = i.peca?.sku === SKU_CORINGA || Boolean(i.descricao_avulsa);

  const salvar = useMutation({
    mutationFn: async () => {
      const q = paraNumero(quantidade);
      if (q === null || q <= 0) throw new Error('Quantidade precisa ser maior que zero.');
      if (avulsa && !nome.trim()) {
        throw new Error('Escreva o nome da peça diversa: é o que o cliente vai ler no papel.');
      }
      // LINHA INTOCADA MANTÉM O NÚMERO EXATO que o cliente combinou. "Os três
      // por 100" volta da divisão como 33,33 cada, e regravar 99,99 num item
      // que ninguém editou mudaria o combinado por um centavo a cada abertura
      // da tela. Só recalcula quem mexeu na quantidade ou no preço.
      const mexeram = quantidade !== qtdInicial || precoUnit !== precoInicial;
      await atualizarItemEncomenda(i.id, {
        quantidade: q,
        valor: mexeram
          ? combinadoDaLinha(paraNumero(precoUnit), q)
          : (i.valor === null ? null : Number(i.valor)),
        ...(avulsa ? {
          descricao_avulsa: nome.trim(),
          // 071: vazio grava NULL — apagar o código é uma escolha, não um erro.
          codigo_avulso: codigo.trim() || null,
        } : {}),
      });
    },
    onSuccess: aoMudar,
  });
  const tirar = useMutation({
    mutationFn: () => removerItemEncomenda(i.id),
    onSuccess: aoMudar,
  });

  const tabela = i.peca?.preco_balcao == null ? null : Number(i.peca.preco_balcao);

  return (
    <li className="border border-edge p-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {/* O CÓDIGO DA PEÇA (10/09/2026, pedido do dono): na diversa é o que
              o vendedor digitou; na cadastrada, o do fabricante — que é o que
              se usa para pedir ao fornecedor. O sku (código da loja) fica ao
              lado, menor, porque serve para outra coisa: bipar a etiqueta. */}
          {avulsa ? (
            <p className="text-xs font-bold uppercase tracking-wider text-signal-soft">
              Peça sem cadastro
            </p>
          ) : (
            <>
              <p className="truncate text-sm font-bold">{nomeDaPecaEncomendada(i)}</p>
              <CodigoDaPeca codigo={codigoDoItem(i)} />
            </>
          )}
        </div>
        {!congelada && (
          <button type="button" className="shrink-0 px-2 text-xs uppercase text-erro"
                  disabled={tirar.isPending} onClick={() => tirar.mutate()}>
            tirar
          </button>
        )}
      </div>

      {avulsa && (
        <div className="mt-1 grid gap-2 sm:grid-cols-2">
          <div className="min-w-0">
            <label className="rotulo" htmlFor={`nome-item-${i.id}`}>Que peça é esta? *</label>
            <input id={`nome-item-${i.id}`} className="campo py-2" value={nome} disabled={congelada}
                   placeholder="Ex.: Retrovisor Uno 98 LD"
                   onChange={(ev) => setNome(ev.target.value)} />
          </div>
          {/* 071: o código sai do nome e ganha campo. O nome é o que o cliente
              lê no papel de retirada; o código é o que a loja usa para pedir a
              peça ao fornecedor. */}
          <div className="min-w-0">
            <label className="rotulo" htmlFor={`codigo-item-${i.id}`}>Código da peça (não sai no papel)</label>
            <input id={`codigo-item-${i.id}`} className="campo py-2 font-mono" value={codigo}
                   disabled={congelada} placeholder="Ex.: 9.4212.20"
                   autoCapitalize="characters" autoComplete="off"
                   onChange={(ev) => setCodigo(ev.target.value)} />
          </div>
        </div>
      )}

      <div className="mt-1 grid grid-cols-[5rem_1fr_auto] items-end gap-2">
        <div>
          <label className="rotulo" htmlFor={`qtd-item-${i.id}`}>Qtd.</label>
          <input id={`qtd-item-${i.id}`} className="campo py-2" inputMode="decimal"
                 value={quantidade} disabled={congelada}
                 onChange={(ev) => setQuantidade(ev.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor={`valor-item-${i.id}`}>Preço un. (R$)</label>
          <input id={`valor-item-${i.id}`} className="campo py-2" inputMode="decimal"
                 placeholder="a combinar" value={precoUnit} disabled={congelada}
                 onChange={(ev) => setPrecoUnit(ev.target.value)} />
        </div>
        {!congelada && (
          <button type="button" className="botao-primario px-3 py-2 text-sm"
                  disabled={salvar.isPending} onClick={() => salvar.mutate()}>
            {salvar.isPending ? '…' : 'Salvar'}
          </button>
        )}
      </div>

      <ContaDaLinha quantidade={paraNumero(quantidade)} precoUnit={paraNumero(precoUnit)} />

      {tabela !== null && !congelada && paraNumero(precoUnit) !== tabela && (
        <button type="button" className="mt-1 text-xs text-signal-soft underline"
                onClick={() => setPrecoUnit(paraTexto(tabela))}>
          usar tabela: {brl(tabela)}
        </button>
      )}

      {salvar.error ? <div className="mt-1"><Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso></div> : null}
      {tirar.error ? <div className="mt-1"><Aviso tipo="erro">{mensagemDeErro(tirar.error)}</Aviso></div> : null}
    </li>
  );
}
